import { useAppSelector } from '@app/core/hooks/redux-hooks';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import {
  setIrrHurdleCalculationsTableExpandStates,
  setSponsorIrrAndHurdleSelectedTab,
} from '@app/core/store/ui-values-slice';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { FC, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import classNames from 'classnames';
import {
  TABLE_COL_WIDTH,
  TABLE_LABELS_COL_WIDTH_ALTERNATIVE,
} from '@app/shared/constants/table-config';
import TrancheSection from './TrancheSection';
import IRRHurdleTableHeader from './IRRHurdleTableHeader';
import useIsIrrTranchePresent from '@app/shared/hooks/useIsIrrTranchePresent';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { useIRRHurdleCalculationsData } from './hooks/useIRRHurdleCalculationsData';
import { useInstrumentOptions } from './hooks/useInstrumentOptions';
import { useInstrumentForm } from './hooks/useInstrumentForm';
import { calculateYearEntries } from './utils/yearEntries';
import { useExpansionState } from '@app/shared/hooks/useExpansionState';
import { useFocusMode } from '@app/shared/hooks/focus-mode';

const PARENT_CLASSNAME = 'irr-hurdle-calculations-table';

const IRRHurdleCalculations: FC = () => {
  const { generatePath } = usePwcNavigate();
  useSetSelectedTab(RouteConstants.IRRHurdleCalculations, setSponsorIrrAndHurdleSelectedTab);

  const { isActive, isPaused, focusType } = useFocusMode();
  const isBannerVisible = isActive || isPaused;

  const isIRRTranchePresent = useIsIrrTranchePresent();

  const { instrumentOptions, isOpmOnly } = useInstrumentOptions(true);
  const { formMethods, selectedInstrument, navigatedSelectedHurdle, navigatedSelectedYear } =
    useInstrumentForm(instrumentOptions);
  const { groupedByConditionAndExitDate } = useIRRHurdleCalculationsData(selectedInstrument);

  const instrumentConditionReferences = useMemo(
    () => Object.keys(groupedByConditionAndExitDate),
    [groupedByConditionAndExitDate]
  );

  const { handleToggle, isExpanded, itemRefs } = useExpansionState({
    itemIds: instrumentConditionReferences,
    storedExpandedIds: useAppSelector((state) => state.uiValues.irrHurdleCalculationsExpandStates),
    saveExpandedIds: setIrrHurdleCalculationsTableExpandStates,
    focusedItemId: isActive ? navigatedSelectedHurdle : null,
  });

  const yearEntries = useMemo(
    () => calculateYearEntries(groupedByConditionAndExitDate),
    [groupedByConditionAndExitDate]
  );

  if (!isIRRTranchePresent) {
    const newPath = generatePath(`../../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  const renderYearCols = () => {
    const cols: React.ReactNode[] = [];
    const entries = Object.entries(yearEntries);
    const totalEntries = entries.length;

    entries.forEach(([year, count], yearIdx) => {
      for (let i = 0; i < count; i++) {
        cols.push(<col key={`${year}-${i}`} style={{ width: `${TABLE_COL_WIDTH}px` }} />);
      }

      if (yearIdx < totalEntries - 1) {
        cols.push(
          <col
            key={`separator-${yearIdx}`}
            className={styles[`${PARENT_CLASSNAME}__col--separator`]}
          />
        );
      }
    });
    return cols;
  };

  return (
    <table
      className={classNames(
        'table-primary',
        'table-primary--zebra',
        'table-primary--title-column-nowrap',
        'table-primary--sticky-first-column',
        styles[PARENT_CLASSNAME]
      )}>
      <colgroup>
        <col style={{ minWidth: `${TABLE_LABELS_COL_WIDTH_ALTERNATIVE}px` }} />
        {renderYearCols()}
      </colgroup>
      <IRRHurdleTableHeader
        formMethods={formMethods}
        instrumentOptions={instrumentOptions}
        yearEntries={yearEntries}
        navigatedSelectedYear={navigatedSelectedYear}
        focusModeType={isBannerVisible ? focusType : undefined}
        isOpmOnly={isOpmOnly}
        parentClassName={PARENT_CLASSNAME}
      />
      <tbody>
        {Object.entries(groupedByConditionAndExitDate).map(([conditionReference, condition]) => (
          <TrancheSection
            key={conditionReference}
            condition={condition}
            yearEntries={yearEntries}
            isExpanded={isExpanded(conditionReference)}
            onToggle={() => handleToggle(conditionReference)}
            lastRowRef={(el: HTMLTableRowElement | null) => {
              itemRefs.current[conditionReference] = el;
            }}
          />
        ))}
      </tbody>
    </table>
  );
};

export default IRRHurdleCalculations;
